import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@verily-src/react-design-system";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import useComplianceSettingDataLoader from "./ComplianceSettingDataLoader";
import { AppRoutes } from "core/AppRoutes";
import { useNavigate } from "react-router-dom";
import {
  WearTimeSettingCard,
  WearTimeSettingCardRef,
} from "./WearTimeSettingCard";
import { SyncTimeSettingCard } from "./SyncTimeSettingCard";
import {
  SurveySubmissionSettingCard,
  SurveySubmissionSettingCardRef,
} from "./SurveySubmissionSettingCard";
import {
  MedsLoggingSettingCard,
  MedsLoggingSettingCardRef,
} from "./MedsLoggingSettingCard";
import { ComplianceSetting } from "generated/compliancesetting/compliancesetting_pb";
import ConfirmComplianceSettingDialog from "./ConfirmComplianceSettingDialog";
import { featureFlagProvider } from "core/FeatureFlagProvider";
import { setFromScreen, setShowSuccessMessage } from "./ComplianceSettingSlice";
import { focusOnRef, sxFocusWithin } from "common/Accessibility";
import { usePageTitle } from "components/PageTitle";
import TopAppBar from "components/TopAppBar";
import { FocusOn, setFocusTarget } from "common/CommonViewState";
import {
  ECGReadingSettingsCard,
  ECGReadingsSettingCardRef,
} from "./ECGReadingSettingCard";

interface TitleBarProps {
  disableSaveButton: boolean;
  scrollTarget?: HTMLDivElement;
  onClickSave(): void;
}

const TitleBar: React.FC<TitleBarProps> = ({
  disableSaveButton,
  scrollTarget,
  onClickSave,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const setComplianceParametersTitleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    focusOnRef(setComplianceParametersTitleRef);
  }, []);

  return (
    <TopAppBar scrollTarget={scrollTarget}>
      <Toolbar
        disableGutters
        sx={{
          background: theme.palette.background.canvas,
          paddingY: "24px",
          paddingX: "40px",
        }}
      >
        <Box sx={sxFocusWithin}>
          <IconButton
            onClick={() => {
              navigate(AppRoutes.COMPLIANCE_SETTING);
            }}
          >
            <CloseIcon titleAccess="Close" />
          </IconButton>
        </Box>
        <Typography
          component="h1"
          variant="display5"
          sx={{ marginLeft: "12px" }}
          ref={setComplianceParametersTitleRef}
          tabIndex={-1}
        >
          Set compliance parameters
        </Typography>
        <Box sx={{ flexGrow: "1" }}></Box>
        <Button
          variant="outlined"
          label="Cancel"
          onClick={() => {
            dispatch(setFromScreen(AppRoutes.EDIT_COMPLIANCE_SETTING));
            navigate(AppRoutes.COMPLIANCE_SETTING);
          }}
          sx={{
            marginX: "12px",
          }}
        />
        <Button
          variant="filled"
          label="Save"
          disabled={disableSaveButton}
          onClick={onClickSave}
        />
      </Toolbar>
    </TopAppBar>
  );
};

const EditComplianceSetting: React.FC = () => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const appState = useAppSelector((state) => state.appState);
  const currentRegistryId = useAppSelector(
    (state) => state.userConfig.selectedRegistryId
  );

  const { isLoading, hasError, complianceSetting, loadComplianceSetting } =
    useComplianceSettingDataLoader();

  // Set page title.
  usePageTitle("Set compliance parameters");

  const navigate = useNavigate();

  // Redirect to home screen when page is refreshed.
  useEffect(() => {
    if (!appState.userConfigured) {
      navigate(AppRoutes.ROOT);
    }
  }, [appState.userConfigured, navigate]);

  // Reload data if currently selected study changes.
  useEffect(() => {
    loadComplianceSetting(currentRegistryId);
  }, [currentRegistryId, loadComplianceSetting]);

  // Set up refs to setting cards.
  const wearTimeSettingCardRef = useRef<WearTimeSettingCardRef>(null);
  const syncTimeSettingCardRef = useRef<WearTimeSettingCardRef>(null);
  const surveySubmissionSettingCardRef =
    useRef<SurveySubmissionSettingCardRef>(null);
  const medsLoggingSettingCardRef = useRef<MedsLoggingSettingCardRef>(null);
  const ecgReadingSettingCardRef = useRef<ECGReadingsSettingCardRef>(null);

  const [newSetting, setNewSetting] = useState(new ComplianceSetting());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleClickSaveButton = useCallback(() => {
    let isInputsValid = true;
    if (!wearTimeSettingCardRef.current!.validateInput()) {
      isInputsValid = false;
    }

    if (!syncTimeSettingCardRef.current!.validateInput()) {
      isInputsValid = false;
    }

    if (featureFlagProvider.isSurveyEnabled(appConfig.environment)) {
      if (!surveySubmissionSettingCardRef.current!.validateInput()) {
        isInputsValid = false;
      }
    }

    if (!medsLoggingSettingCardRef.current!.validateInput()) {
      isInputsValid = false;
    }

    if (!ecgReadingSettingCardRef.current!.validateInput()) {
      isInputsValid = false;
    }

    if (!isInputsValid) {
      return;
    }

    const setting = new ComplianceSetting();
    setting
      .setName("complianceSettings/" + currentRegistryId)
      .setWearTimeDaysPerWeek(wearTimeSettingCardRef.current!.getDaysPerWeek())
      .setWearTimeHoursPerDay(wearTimeSettingCardRef.current!.getHoursPerDay())
      .setSyncDaysPerWeek(syncTimeSettingCardRef.current!.getDaysPerWeek())
      .setSurveySubmissionPercentage(
        featureFlagProvider.isSurveyEnabled(appConfig.environment)
          ? surveySubmissionSettingCardRef.current!.getSurveySubmissionPercentage()
          : 0
      )
      .setMedsLoggedPerDay(medsLoggingSettingCardRef.current!.getDosesPerDay())
      .setMedsLoggedPerWeek(
        medsLoggingSettingCardRef.current!.getDosesPerWeek()
      )
      .setEcgReadingsPerDay(
        ecgReadingSettingCardRef.current!.getECGReadingsPerDay()
      );

    setNewSetting(setting);
    setShowConfirmDialog(true);
  }, [currentRegistryId, appConfig.environment]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  dispatch(setFocusTarget(FocusOn.EditParameters));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "start",
        width: "100%",
        height: "100vh",
      }}
    >
      <TitleBar
        disableSaveButton={isLoading || hasError}
        scrollTarget={containerRef.current ? containerRef.current : undefined}
        onClickSave={() => {
          handleClickSaveButton();
        }}
      />
      <Container
        role="main"
        ref={containerRef}
        sx={{ overflowY: "auto", paddingY: "40px" }}
      >
        {/* Show loading indicator while loading is in progress */}
        {isLoading && (
          <Box
            sx={{
              marginTop: "100px",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {/* Show error screen if there is a loading error */}
        {!isLoading && hasError && (
          <Box
            sx={{
              marginTop: "100px",
            }}
          >
            <Alert severity="error">
              Failed to load compliance parameters. Please try again.
            </Alert>
          </Box>
        )}
        {/* Show compliance setting screen */}
        {!isLoading && !hasError && (
          <Box
            role="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "center",
              width: "100%",
            }}
          >
            <WearTimeSettingCard
              initValue={complianceSetting}
              ref={wearTimeSettingCardRef}
            />
            <SyncTimeSettingCard
              initValue={complianceSetting}
              ref={syncTimeSettingCardRef}
            />
            {featureFlagProvider.isSurveyEnabled(appConfig.environment) && (
              <SurveySubmissionSettingCard
                initValue={complianceSetting}
                ref={surveySubmissionSettingCardRef}
              />
            )}
            <MedsLoggingSettingCard
              initValue={complianceSetting}
              ref={medsLoggingSettingCardRef}
            />
            <ECGReadingSettingsCard
              initValue={complianceSetting}
              ref={ecgReadingSettingCardRef}
            />
          </Box>
        )}
      </Container>
      <ConfirmComplianceSettingDialog
        open={showConfirmDialog}
        registryId={currentRegistryId}
        setting={newSetting}
        hasExistingSetting={complianceSetting !== null}
        onClose={() => {
          setShowConfirmDialog(false);
        }}
        onSuccess={() => {
          dispatch(setFromScreen(AppRoutes.EDIT_COMPLIANCE_SETTING));
          dispatch(setShowSuccessMessage(true));
          navigate(AppRoutes.COMPLIANCE_SETTING);
        }}
      />
    </Box>
  );
};

export default EditComplianceSetting;
